import Cookies from 'js-cookie';
import { useContext, useEffect, useRef, useState } from 'react';
import { FaHome, FaPen, FaSearch } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import PlacesSearch from './PlacesSearch';
import Popup from 'reactjs-popup';
import { BsChevronDown } from 'react-icons/bs';
import useDebounce from 'hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import { FiHome } from 'react-icons/fi';
import ConfirmPopup from '../UI/ConfirmPopup';
import { useAPIData } from 'hooks/useAPIData';
import { usePerms } from 'contexts/PermsContext';
import useDataRefresh from 'hooks/useDataRefresh';
import { ColorContext } from 'App';
import { getUserPlacesData, updateUserPlacesData } from 'services/user/places.service';
import { informLocationChange } from 'services/location/location.service';

const PlacesSelector = ({ widgetRefresh, setWidgetRefresh }) => {
  const navigate = useNavigate();
  const [places, setPlaces] = useState([]);
  const [location, setLocation] = useState({});
  const [searching, setSearching] = useState(false);
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [showWaitingForData, setShowWaitingForData] = useState(false);
  const [waitingForDataMessage, setWaitingForDataMessage] = useState('Loading...');
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);
  const debouncedWidgetRefresh = useDebounce(widgetRefreshKey, 1000);
  const FusionData = useAPIData();
  const perms = usePerms();
  const dataRefresh = useDataRefresh();
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (debouncedWidgetRefresh) {
      (async () => {
        setWidgetRefresh(debouncedWidgetRefresh);
        dataRefresh.setWidgetRefresh(debouncedWidgetRefresh);
      })();
    }
  }, [debouncedWidgetRefresh]);

  useEffect(() => {
    (async () => {
      const response = await getUserPlacesData();

      const placeList = response.data.places || [];
      setPlaces(placeList);
      if (Cookies.get('selectedLocationIndex') > placeList.length - 1) {
        Cookies.set('selectedLocationIndex', 0);
      }

      if (!Cookies.get('selectedLocationIndex')) {
        Cookies.set('selectedLocationIndex', 0);
      }

      if (placeList.length === 0) {
        addDefaultLocationIfEmpty();
      } else {
        selectLocation(placeList[Cookies.get('selectedLocationIndex')]);
      }
    })();
  }, []);

  const selectLocation = (location) => {
    if (!location) return;

    if (eventSourceRef.current) eventSourceRef.current.close();

    informLocationChange(location);
    setLocation(location);

    setWidgetRefreshKey(new Date().toString());

    const locationCheck = FusionData.isLocationReady({
      location: location,
      fetchCallback: (message) => {
        setShowWaitingForData(true);
        setWaitingForDataMessage(message);
      },
      errorCallback: (message) => {
        setShowWaitingForData(true);
        setWaitingForDataMessage(message);
      },
      successCallback: (message) => {
        setShowWaitingForData(false);
        setWidgetRefreshKey(new Date().toString());
        setLocation(location);
      },
    });
    if (locationCheck) eventSourceRef.current = locationCheck.eventSource;
  };

  const addLocation = (location) => {
    if (places.find((place) => place.place_id === location.place_id)) return;

    let tempPlaces = [...places];
    tempPlaces.push(location);
    tempPlaces.sort((a, b) => {
      if (a.DefaultLocationUserHome === true) return -1;
      if (b.DefaultLocationUserHome === true) return 1;
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    });
    setPlaces([...tempPlaces]);
    updateUserPlacesData([...tempPlaces]);
    const index = tempPlaces.indexOf(location);
    Cookies.set('selectedLocationIndex', index);
    selectLocation(location);
  };

  const changeHomeLocation = (location) => {
    let tempPlaces = [...places];
    tempPlaces = tempPlaces.map((place) => {
      place.DefaultLocationUserHome = false;
      return place;
    });
    let index = tempPlaces.indexOf(location);
    if (index > -1) {
      tempPlaces[index].DefaultLocationUserHome = true;
    }
    tempPlaces = tempPlaces.sort((a, b) => {
      if (a.DefaultLocationUserHome === true) return -1;
      if (b.DefaultLocationUserHome === true) return 1;
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    });

    setPlaces([...tempPlaces]);
    updateUserPlacesData([...tempPlaces]);
    selectLocation(tempPlaces[0]);
    Cookies.set('selectedLocationIndex', 0);
  };

  const addDefaultLocationIfEmpty = () => {
    let defaultPlace = {
      city: 'Dallas',
      DefaultLocationUserHome: false,
      term: 'Dallas',
      type: 'locality',
      title: 'Dallas',
      geometry: {
        location: {
          lat: 32.7766642,
          lng: -96.7969879,
        },
        viewport: {
          northeast: {
            lat: 33.0237920649461,
            lng: -96.46375805411309,
          },
          southwest: {
            lat: 32.6175369971454,
            lng: -96.9993469629029,
          },
        },
      },
      latitude: 32.7766642,
      place_id: 'ChIJS5dFe_cZTIYRj2dH9qSb7Lk',
      longitude: -96.7969879,
      place_data: {
        name: 'Dallas',
        types: ['locality', 'political'],
        geometry: {
          location: {
            lat: 32.7766642,
            lng: -96.7969879,
          },
          viewport: {
            northeast: {
              lat: 33.0237920649461,
              lng: -96.46375805411309,
            },
            southwest: {
              lat: 32.6175369971454,
              lng: -96.9993469629029,
            },
          },
        },
        formatted_address: 'Dallas, TX, USA',
        address_components: [
          {
            types: ['locality', 'political'],
            long_name: 'Dallas',
            short_name: 'Dallas',
          },
          {
            types: ['administrative_area_level_2', 'political'],
            long_name: 'Dallas County',
            short_name: 'Dallas County',
          },
          {
            types: ['administrative_area_level_1', 'political'],
            long_name: 'Texas',
            short_name: 'TX',
          },
          {
            types: ['country', 'political'],
            long_name: 'United States',
            short_name: 'US',
          },
        ],
      },
      state_long: 'Texas',
      state_short: 'TX',
    };
    if (places.length === 0) {
      setPlaces([defaultPlace]);
      updateUserPlacesData([defaultPlace]);
      setLocation(defaultPlace);
    }
  };

  const theme = useContext(ColorContext);

  const permsData = perms.data;

  if (!permsData) return null;
  return (
    <div>
      {!searching && location && (
        <Popup
          on={'click'}
          trigger={
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              {location !== places[0] && (
                <div
                  className="d-flex align-items-center"
                  onClick={() => selectLocation(places[0])}
                >
                  <FaHome />
                </div>
              )}
              <div
                className="d-flex justify-content-between px-2 py-1"
                style={{
                  backgroundColor: theme.primaryHighlight,
                  borderRadius: 30,
                  fontSize: 18,
                  color: theme.fontColor,
                }}
              >
                <div className="d-flex">
                  <div
                    className="rounded-circle align-content-center flex-shrink-0"
                    style={{
                      background: theme.background,
                      height: '24px',
                      width: '24px',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      {location.DefaultLocationUserHome ? (
                        <FaHome size={14} />
                      ) : (
                        <FaLocationDot size={14} />
                      )}{' '}
                    </div>
                  </div>
                  <Popup
                    trigger={
                      <div className="mx-2 text-truncate w-100" style={{ width: 100 }}>
                        {location.title}
                      </div>
                    }
                    on={'hover'}
                    position={'top center'}
                    closeOnDocumentClick
                    closeOnEscape
                  >
                    <div className="px-2 py-1 rounded-3 bg-white text-black">{location.title}</div>
                  </Popup>
                </div>
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                  {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) && (
                    <div
                      onClick={() => {
                        navigate('/usermenu?tab=Places');
                      }}
                    >
                      <FaPen />
                    </div>
                  )}
                  <div
                    onClick={() => {
                      setSearching(true);
                      setDropDownOpen(false);
                    }}
                  >
                    <FaSearch />
                  </div>
                  {places.length > 1 && <BsChevronDown />}
                </div>
              </div>
            </div>
          }
          open={dropDownOpen}
          onOpen={() => setDropDownOpen(true)}
          onClose={() => setDropDownOpen(false)}
          position={'bottom center'}
          closeOnDocumentClick
          closeOnEscape
        >
          {places.length > 1 && (
            <div className="overflow-y-auto" style={{ height: 200, borderRadius: 10 }}>
              {places &&
                places.length > 0 &&
                places.map((place, index) => (
                  <div
                    key={index}
                    value={index}
                    className="d-flex p-1 cursor-pointer"
                    style={{
                      backgroundColor: theme.primaryHighlight,
                      width: 300,
                      borderBottom: index !== places.length - 1 && '1px solid white',
                      color: theme.fontColor,
                    }}
                    onClick={() => {
                      selectLocation(place);
                      setDropDownOpen(false);
                      Cookies.set('selectedLocationIndex', index);
                    }}
                  >
                    <div className="d-flex justify-content-between w-100 ms-1">
                      <div>{place.title}</div>
                      {!place.DefaultLocationUserHome &&
                        perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable']) && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              changeHomeLocation(place);
                            }}
                          >
                            <FiHome />
                          </div>
                        )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Popup>
      )}
      {searching && (
        <div>
          <PlacesSearch
            places={places}
            addLocation={addLocation}
            setSearching={setSearching}
            selectLocation={selectLocation}
            setWidgetRefresh={setWidgetRefresh}
            quickLook
          />
        </div>
      )}
      {showWaitingForData && (
        <ConfirmPopup
          open={showWaitingForData}
          setOpen={setShowWaitingForData}
          message={waitingForDataMessage}
          options={['Cancel']}
          callbacks={[
            () => {
              setShowWaitingForData(false);
              FusionData.cancelLocationInquiry();
            },
          ]}
          closeAction={() => {
            setShowWaitingForData(false);
            FusionData.cancelLocationInquiry();
          }}
          buttonStyles={[{ backgroundColor: theme.background, color: theme.fontColor }]}
        />
      )}
    </div>
  );
};

export default PlacesSelector;
